import { useState, useCallback } from 'react';
import { SS27_LINES, type SS27Line } from '../api/objetivosApi.js';

export interface AgentExportRow {
  agentId: string;
  agentName: string;
  byLine: Record<SS27Line, { facturacion: number; clientes: number; clientesNuevos: number }>;
  totalFact: number;
  totalCli: number;
  totalNew: number;
  necesidades: string;
}

/**
 * Export the SS27 questionnaire responses to a real .xlsx workbook.
 * SheetJS is lazy-loaded so it doesn't bloat the initial bundle.
 *
 * Two sheets:
 *   - "Objetivos SS27": one row per agent × line with the three metrics.
 *   - "Necesidades": one row per agent with the free-text answer.
 */
export function useExportExcel() {
  const [exporting, setExporting] = useState(false);

  const exportExcel = useCallback(async (rows: AgentExportRow[], filename: string) => {
    setExporting(true);
    try {
      const XLSX = await import('xlsx');

      // Sheet 1 — flat per agent × line.
      const flat: Array<Record<string, string | number>> = [];
      for (const a of rows) {
        for (const line of SS27_LINES) {
          const c = a.byLine[line];
          flat.push({
            'Agente ID': a.agentId,
            'Agente': a.agentName,
            'Línea': line,
            'Facturación prevista (€)': c.facturacion,
            'Nº clientes': c.clientes,
            'Clientes nuevos': c.clientesNuevos,
          });
        }
        // total row per agent
        flat.push({
          'Agente ID': a.agentId,
          'Agente': a.agentName,
          'Línea': 'TOTAL',
          'Facturación prevista (€)': a.totalFact,
          'Nº clientes': a.totalCli,
          'Clientes nuevos': a.totalNew,
        });
      }

      const ws1 = XLSX.utils.json_to_sheet(flat);
      ws1['!cols'] = [
        { wch: 12 }, { wch: 28 }, { wch: 10 }, { wch: 22 }, { wch: 12 }, { wch: 16 },
      ];

      // Sheet 2 — needs.
      const needs = rows
        .filter((a) => a.necesidades)
        .map((a) => ({ 'Agente': a.agentName, 'Necesidades': a.necesidades }));
      const ws2 = XLSX.utils.json_to_sheet(needs.length ? needs : [{ Agente: '', Necesidades: '' }]);
      ws2['!cols'] = [{ wch: 28 }, { wch: 80 }];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws1, 'Objetivos SS27');
      XLSX.utils.book_append_sheet(wb, ws2, 'Necesidades');
      XLSX.writeFile(wb, filename);
    } finally {
      setExporting(false);
    }
  }, []);

  return { exportExcel, exporting };
}
